<template>
  <modal-dialog
    size="lg"
    ref="modalTarget"
    heading="User role"
    heading-icon="edit"
    color="primary"
    showHeaderSeparator
    :has-opener="false"
  >
    <template #modal-body>
      <div class="my-6 space-y-6">
        <user-details-card
          :user="user"
          :roles="userRoles"
        />
        <form-kit
          id="update-user-role-form"
          type="form"
          :actions="false"
          autocomplete="off"
          form-class="space-y-3"
          :incomplete-message="false"
          @submit="handleUpdateUserRole"
          #default="{ state, value }"
        >
          <div class="space-y-2">
            <p class="label is-required">User role</p>
            <div class="space-y-2">
              <template v-if="isLoadingRoles">
                <div
                  v-for="i in 4"
                  :key="i"
                  class="skeleton h-16.5 w-full rounded-lg"
                />
              </template>
              <template v-else>
                <form-kit
                  type="hidden"
                  name="role_id"
                  v-model="form.role_id"
                  validation="required"
                />
                <radio-label
                  v-for="role in roleOptions"
                  :key="role.id"
                  :value="role.value"
                  v-model="form.role_id"
                  class="px-4 py-3 ring ring-gray-200 has-checked:ring-2 has-checked:ring-primary"
                >
                  <div class="space-y-0.5">
                    <p class="font-semibold text-base-content">{{ role.label }}</p>
                    <p>{{ role.description }}</p>
                  </div>
                </radio-label>
                <span
                  v-if="state.submitted && !value.role_id"
                  class="text-red-500 text-xs"
                  >Please select a user role</span
                >
              </template>
            </div>
          </div>
        </form-kit>
      </div>
    </template>
    <template #primary-action>
      <form-button
        class="btn-primary"
        @click="$formkit.submit('update-user-role-form')"
        :disabled="isUpdatingUserRole"
        :loading="isUpdatingUserRole"
        loading-text="Saving changes..."
      >
        Save changes
      </form-button>
    </template>
  </modal-dialog>
</template>

<script setup>
import { ref, computed, reactive, watch } from 'vue';
import { slugify } from '@/utils/string';
import { useRoles } from '@/services/suppliers/queries';
import { useToast } from '@/composables/useToast';
import { useUpdateUserRole } from '@/services/users/queries';
import { UserRoleDescriptions } from '@/constants/users';
import { getUserName } from '@/utils/users';
import RadioLabel from '@/components/form/RadioLabel.vue';
import UserDetailsCard from '@/components/users/UserDetailsCard.vue';

const props = defineProps({
  user: {
    type: Object,
    default: () => ({}),
  },
  userRoles: {
    type: Array,
    default: () => [],
  },
});

const form = reactive({
  role_id: null,
});

const modalTarget = ref(null);

const { toast } = useToast();
const { mutate: updateUserRole, isPending: isUpdatingUserRole } = useUpdateUserRole();
const { data: rolesData, isLoading: isLoadingRoles } = useRoles(computed(() => modalTarget.value?.isModalOpen));

const roleOptions = computed(() => {
  return (
    rolesData.value?.map((role) => ({
      value: role.id,
      label: role.name,
      description: UserRoleDescriptions[slugify(role.name)],
    })) || []
  );
});

const userId = computed(() => props.user.user_id);
const userName = computed(() => getUserName(props.user));

const handleUpdateUserRole = () => {
  updateUserRole(
    { user_id: userId.value, role_ids: [form.role_id] },
    {
      onSuccess: () => {
        toast({
          heading: 'User role updated',
          message: `${userName.value}'s user role was updated successfully.`,
          color: 'success',
          icon: 'check-circle',
        });
        modalTarget.value?.closeModal();
      },
    }
  );
};

watch(
  () => props.userRoles,
  (newRoles) => {
    if (newRoles.length) {
      form.role_id = newRoles.at(0).id;
    }
  },
  { immediate: true }
);

defineExpose({
  showModal: () => {
    modalTarget.value?.showModal();
  },
});
</script>
